import type { PreviewContract } from "../contract";
import { getPopoverStyles } from "./styles";
import type { ThemeOverrides } from "./types";

export interface PopoverRenderer {
  element: HTMLDivElement;
  contains(node: Node | null): boolean;
  showLoading(): void;
  showData(data: PreviewContract): void;
  hide(): void;
  destroy(): void;
}

function escapeHtml(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

function renderPreviewMarkup(data: PreviewContract): string {
  const mediaMarkup = data.media
    ? `<img src="${escapeHtml(data.media)}" alt="" />`
    : "";

  const subtitleMarkup = data.subtitle
    ? `<p class="subtitle">${escapeHtml(data.subtitle)}</p>`
    : "";

  const descriptionMarkup = data.description
    ? `<p class="description">${escapeHtml(data.description)}</p>`
    : "";

  return `
    <div class="card" role="dialog" aria-label="Book preview">
      <div class="media">${mediaMarkup}</div>
      <div class="content">
        <p class="title">${escapeHtml(data.title)}</p>
        ${subtitleMarkup}
        ${descriptionMarkup}
      </div>
    </div>
  `;
}

const LOADING_MARKUP = `
  <div class="card" role="status" aria-live="polite" aria-label="Loading preview">
    <div class="media skeleton"></div>
    <div class="content">
      <div class="skeleton skeleton-line"></div>
      <div class="skeleton skeleton-line short"></div>
      <div class="skeleton skeleton-line"></div>
    </div>
  </div>
`;

export function createPopoverRenderer(theme?: ThemeOverrides): PopoverRenderer {
  const host = document.createElement("div");
  host.dataset.librariumPreviewPopover = "";
  host.style.display = "none";
  host.style.left = "0";
  host.style.pointerEvents = "none";
  host.style.position = "fixed";
  host.style.top = "0";
  host.style.zIndex = "2147483647";

  const shadowRoot = host.attachShadow({ mode: "open" });
  const style = document.createElement("style");
  style.textContent = getPopoverStyles(theme);

  const container = document.createElement("div");
  shadowRoot.append(style, container);

  (document.body ?? document.documentElement).append(host);

  return {
    element: host,

    contains(node) {
      return Boolean(
        node && (host.contains(node) || shadowRoot.contains(node)),
      );
    },

    showLoading() {
      container.innerHTML = LOADING_MARKUP;
      host.style.display = "block";
    },

    showData(data) {
      container.innerHTML = renderPreviewMarkup(data);
      host.style.display = "block";
    },

    hide() {
      host.style.display = "none";
      container.innerHTML = "";
    },

    destroy() {
      host.remove();
    },
  };
}
